import { HISTORY_PLACES } from "./historyPlaces.js";

// Curated release of the archive. Every published image is accompanied by its
// originating record and reuse assessment. Uncleared images are references only.
// The existing HISTORY_PLACES IDs are the one canonical link to map coordinates.
export const ARCHIVAL_MEDIA = {
  market1942: {
    src: "/archive/rio-piedras-market-1942.jpg",
    date: "1942",
    titleEs: "Mercado de productos, Río Piedras",
    titleEn: "Produce market, Río Piedras",
    credit: "Jack Delano / Library of Congress, FSA/OWI Collection",
    origin: "https://www.loc.gov/pictures/item/2017797868/",
    rights: "No known restrictions — Library of Congress record",
    rightsStatus: "usable",
    noteEs: "La descripción del archivo identifica un mercado de productos en Río Piedras; no confirma el punto exacto desde el cual se tomó la foto.",
    noteEn: "The archival caption identifies a produce market in Río Piedras; the precise camera position is not confirmed.",
  },
  plaza1920: {
    src: "/archive/rio-piedras-plaza-1920.jpg",
    date: "c. 1920",
    titleEs: "Plaza de Río Piedras, postal histórica",
    titleEn: "Río Piedras town square, historical postcard",
    credit: "Attilio Moscioni / Wikimedia Commons",
    origin: "https://commons.wikimedia.org/wiki/File:San_Juan_-_Plaza_de_Rio_Piedras.jpg",
    rights: "Public domain (US) — published 1920",
    rightsStatus: "usable",
    noteEs: "Postal identificada por el archivo como la plaza de Río Piedras.",
    noteEn: "A postcard catalogued as the Río Piedras town square.",
  },
  university1920: {
    src: "/archive/upr-rio-piedras-1920.jpg",
    date: "c. 1920",
    titleEs: "Universidad de Puerto Rico, Río Piedras",
    titleEn: "University of Puerto Rico, Río Piedras",
    credit: "Antonio Arguinzoni / Wikimedia Commons",
    origin: "https://commons.wikimedia.org/wiki/File:Rio_Piedras_-_University_of_Puerto_Rico.jpg",
    rights: "Public domain (US) — published before 1931",
    rightsStatus: "usable",
    noteEs: "Postal de época. No representa necesariamente el aspecto actual del recinto.",
    noteEn: "Period postcard; it does not necessarily show the present-day campus.",
  },
  university1939: {
    src: "/archive/upr-rio-piedras-1939.jpg",
    date: "1939",
    titleEs: "Universidad de Puerto Rico, postal de 1939",
    titleEn: "University of Puerto Rico, 1939 postcard",
    credit: "Carbia Brothers (editor) / Wikimedia Commons",
    origin: "https://commons.wikimedia.org/wiki/File:Rio_Piedras_-_University_of_Puerto_Rico_(1939).jpg",
    rights: "Public domain (US) — published without copyright notice (Commons assessment)",
    rightsStatus: "usable",
    noteEs: "Postal del recinto de Río Piedras editada en 1939.",
    noteEn: "Postcard of the Río Piedras campus published in 1939.",
  },
};

export const HISTORY_FEATURES = {
  "history-rio-piedras": {
    slug: "plaza-del-mercado-rio-piedras",
    type: "Comercio / Comunidad",
    tagEn: "Commerce / Community",
    titleEs: "El mercado donde se encontraba Río Piedras",
    titleEn: "The market where Río Piedras met",
    dekEs: "Una fotografía de 1942 abre una ventana a la vida comercial y cotidiana del antiguo pueblo.",
    dekEn: "A photograph from 1942 opens a window into the town's commercial and everyday life.",
    imageIds: ["market1942"],
    introEs: "Antes de ser un punto en el mapa, el mercado era un lugar de intercambio: productos, trabajo y conversaciones diarias.",
    introEn: "Long before it was a point on a map, the market was a place of exchange: produce, work, and everyday conversations.",
    paragraphsEs: [
      "En enero de 1942, el fotógrafo Jack Delano documentó un mercado de productos en Río Piedras para la colección que hoy custodia la Biblioteca del Congreso. El registro ofrece una evidencia visual concreta de la actividad comercial del pueblo durante la primera mitad del siglo XX.",
      "La plaza del mercado fue uno de los espacios asociados a la vida económica y comunitaria de Río Piedras. Los archivos fotográficos del período de Felisa Rincón de Gautier también contienen imágenes de las plazas de mercado de San Juan, Santurce y Río Piedras.",
      "Lo que la fotografía no confirma es la ubicación exacta del fotógrafo. Por eso, la imagen se vincula al mercado histórico como contexto documental, sin presentar la toma como una reproducción exacta de la fachada que hoy encontramos.",
      "Hoy, explorar el mercado es otra forma de leer las transformaciones del centro urbano: el comercio permanece como parte de la memoria colectiva del lugar."
    ],
    paragraphsEn: [
      "In January 1942, photographer Jack Delano documented a produce market in Río Piedras for what is now held in the Library of Congress collection. The image is a tangible visual record of commerce in the town in the first half of the twentieth century.",
      "The market was one of the spaces tied to Río Piedras's economic and community life. Photographic archives from the Felisa Rincón de Gautier era also include market images from San Juan, Santurce, and Río Piedras.",
      "The archival record does not establish the photographer's exact position. We therefore connect the photograph to the market as documentary context, not as an exact then-and-now match to the present building.",
      "Exploring the market today offers another way to read the changes in the historic urban center and the role of commerce in its collective memory."
    ],
    sources: [
      { label: "Library of Congress — Jack Delano, 1942", url: "https://www.loc.gov/pictures/item/2017797868/" },
      { label: "Instituto de Cultura Puertorriqueña — Colección Felisa Rincón de Gautier", url: "https://www.coleccionesicp.pr.gov/colecciones-del-archivo/obras-publicas-2" },
    ],
  },
  "history-plaza-convalecencia": {
    slug: "plaza-de-la-convalecencia",
    type: "Espacio público / Memoria",
    tagEn: "Public space / Memory",
    titleEs: "La plaza de Río Piedras, hacia 1920",
    titleEn: "Río Piedras town square, circa 1920",
    dekEs: "Una postal centenaria y un espacio público que sigue ubicando el corazón del antiguo pueblo.",
    dekEn: "A century-old postcard and the public square at the heart of the former town.",
    imageIds: ["plaza1920"],
    introEs: "La plaza de Río Piedras aparece en una postal atribuida a Attilio Moscioni y fechada alrededor de 1920.",
    introEn: "Río Piedras town square appears in a postcard attributed to Attilio Moscioni and dated around 1920.",
    paragraphsEs: [
      "La postal nos permite observar cómo se representaba la plaza a principios del siglo XX. No hace falta imaginar la escena: este documento sobrevive como una imagen histórica catalogada de Río Piedras.",
      "Río Piedras fue un pueblo independiente antes de incorporarse al municipio de San Juan en 1951. Sus espacios públicos conservaron una identidad propia, vinculada a la vida cívica, religiosa y comercial del centro urbano.",
      "El plano municipal identifica la actual Plaza de la Convalecencia con coordenadas concretas. Ese es el lugar marcado en nuestro mapa; la fotografía sirve como referencia histórica del conjunto urbano, sin afirmar una coincidencia exacta de perspectiva."
    ],
    paragraphsEn: [
      "The postcard offers a view of how the square was represented in the early twentieth century. Rather than relying on a reconstruction, we can look at a surviving historical image catalogued as Río Piedras.",
      "Río Piedras was an independent municipality before its incorporation into San Juan in 1951. Public places continued to carry the identity of the former town and its civic, religious, and commercial life.",
      "The municipal plan identifies the present-day Plaza de la Convalecencia by specific coordinates. That is the place indicated on our map; the postcard is historical context rather than a claimed camera-angle match."
    ],
    sources: [
      { label: "Wikimedia Commons — Attilio Moscioni, c. 1920", url: "https://commons.wikimedia.org/wiki/File:San_Juan_-_Plaza_de_Rio_Piedras.jpg" },
      { label: "Municipio de San Juan — Plan de Mitigación (coordenadas)", url: "https://sanjuan.pr/wp-content/uploads/2026/02/Plan-Mitigacion_MSJ_HMP_Borrador.pdf" },
      { label: "UPR — El Río Piedras agrícola", url: "https://coleccionesuprrpcaribe.omeka.net/exhibits/show/rio-piedras-agricola/trasfondo-rio-piedras" },
    ],
  },
  "history-upr-rio-piedras": {
    slug: "universidad-de-puerto-rico-rio-piedras",
    type: "Educación / Arquitectura",
    tagEn: "Education / Architecture",
    titleEs: "La universidad que transformó Río Piedras",
    titleEn: "The university that reshaped Río Piedras",
    dekEs: "Dos postales históricas muestran distintas etapas de la universidad y su vínculo con el pueblo.",
    dekEn: "Two archival postcards trace different moments of the university and its connection to the town.",
    imageIds: ["university1920", "university1939"],
    introEs: "La universidad forma parte inseparable de la identidad de Río Piedras como centro de estudio, cultura y encuentro.",
    introEn: "The university is inseparable from Río Piedras's identity as a center of education, culture, and gathering.",
    paragraphsEs: [
      "El recinto de Río Piedras comenzó su trayectoria en 1903. A lo largo del siglo XX, la institución se convirtió en una presencia clave en la vida social, cultural y urbana del pueblo.",
      "Las postales conservadas permiten apreciar escenas del campus hacia 1920 y en 1939. Son documentos de momentos distintos; no deben confundirse con fotografías actuales ni con planos exactos de los edificios que existen hoy.",
      "Estudiar estas imágenes junto al mapa moderno permite descubrir cuánto ha cambiado el paisaje urbano y, al mismo tiempo, cómo la presencia universitaria sigue definiendo la zona."
    ],
    paragraphsEn: [
      "The Río Piedras campus traces its beginnings to 1903. Over the twentieth century, the institution became a major presence in the social, cultural, and urban life of the town.",
      "Surviving postcards show the campus around 1920 and in 1939. They document different moments and should not be confused with contemporary photographs or exact plans of surviving buildings.",
      "Viewing these images alongside the modern map reveals how the urban landscape has changed, and how the university remains central to the area's identity."
    ],
    sources: [
      { label: "Wikimedia Commons — Universidad, c. 1920", url: "https://commons.wikimedia.org/wiki/File:Rio_Piedras_-_University_of_Puerto_Rico.jpg" },
      { label: "Wikimedia Commons — Universidad, 1939", url: "https://commons.wikimedia.org/wiki/File:Rio_Piedras_-_University_of_Puerto_Rico_(1939).jpg" },
      { label: "Universidad de Puerto Rico — recinto de Río Piedras", url: "https://www.uprrp.edu/" },
    ],
  },
  "history-acueducto-rio-piedras": {
    slug: "antiguo-acueducto-rio-piedras",
    type: "Patrimonio / Infraestructura",
    tagEn: "Heritage / Infrastructure",
    titleEs: "El antiguo acueducto de Río Piedras",
    titleEn: "The old Río Piedras aqueduct",
    dekEs: "Planos y fotografías históricas conservados en archivos documentan esta infraestructura de agua.",
    dekEn: "Historic drawings and archival photographs document this early water infrastructure.",
    imageIds: [],
    introEs: "La historia urbana también se encuentra en la infraestructura que sostuvo la vida cotidiana.",
    introEn: "Urban history can also be found in the infrastructure that sustained everyday life.",
    paragraphsEs: [
      "El antiguo acueducto es uno de los elementos patrimoniales de Río Piedras. El Archivo Digital PRAHA conserva documentación arquitectónica e imágenes relacionadas con el sistema.",
      "Por ahora, esas imágenes se consultan desde el archivo original, porque sus condiciones de reproducción requieren autorización. Esta ficha mantiene la referencia y el punto geográfico en lugar de publicar una copia sin permiso."
    ],
    paragraphsEn: [
      "The former aqueduct is part of Río Piedras's built heritage. The PRAHA Digital Archive holds architectural drawings and related historical images.",
      "For now, the images are available through the original archive; their reproduction terms require authorization. This record preserves the source and map reference without republishing an uncleared copy."
    ],
    sources: [
      { label: "PRAHA — Antiguo Acueducto de Río Piedras", url: "https://prahadigital.org/s/flmm/item?uid=bdc70ba1-2930-11ef-a756-0242ac190002" },
    ],
  },
};


/**
 * Reader-submitted historic-looking images supplied for the Río Piedras feature.
 * The source, photographer, date, and reuse status have NOT been independently
 * documented, so captions describe what is visible without inventing provenance.
 * These images are separate from source-verified ARCHIVAL_MEDIA and must never
 * be presented as public-domain or as exact before-and-after map matches.
 */
export const RIO_PIEDRAS_SUBMITTED_PHOTOS = [
  {
    id: "rio-submitted-plaza-de-diego-mall",
    src: "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/d21f8280d_IMG_1725.jpeg",
    titleEs: "Plaza de Diego Mall",
    titleEn: "Plaza de Diego Mall",
    captionEs: "Interior de un centro comercial, con escaleras mecánicas y el letrero «Plaza de Diego Mall».",
    captionEn: "An indoor shopping arcade, escalators and a visible “Plaza de Diego Mall” sign.",
  },
  {
    id: "rio-submitted-pedestrian-corridor",
    src: "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/0a1a53373_IMG_1731.jpeg",
    titleEs: "El paseo comercial",
    titleEn: "The shopping promenade",
    captionEs: "Escena histórica de una calle peatonal concurrida, con comercios y gente.",
    captionEn: "A historic-looking pedestrian street scene with storefronts and people.",
  },
  {
    id: "rio-submitted-public-telephones",
    src: "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/c7c70c200_IMG_1730.jpeg",
    titleEs: "La vida en la calle",
    titleEn: "Street life",
    captionEs: "Transeúntes, vitrinas comerciales y una cabina de teléfono público.",
    captionEn: "Pedestrians, shopfronts, and a public telephone booth.",
  },
  {
    id: "rio-submitted-town-square",
    src: "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/67ac2b0a2_IMG_1726.jpeg",
    titleEs: "La plaza y la iglesia",
    titleEn: "The square and the church",
    captionEs: "Vista histórica de una plaza urbana junto a una iglesia con campanario.",
    captionEn: "A historic-looking urban square beside a church with a bell tower.",
  },
];

export const archivePlaceById = (id) => HISTORY_PLACES.find((place) => place.id === id);
export const archiveFeatureByPlace = (id) => HISTORY_FEATURES[id] || null;
export const archiveSlugForPlace = (place) =>
  HISTORY_FEATURES[place.id]?.slug || place.id.replace(/^history-/, "");
export const archivePlaceBySlug = (slug) =>
  HISTORY_PLACES.find((place) => archiveSlugForPlace(place) === slug);
export const archiveLinkForPlace = (place) =>
  "/historia/" + archiveSlugForPlace(place);
export const archiveMapLink = (place) =>
  "/map?history=" + encodeURIComponent(place.id);
export const featuredArchivePlaces = HISTORY_PLACES.filter((place) => HISTORY_FEATURES[place.id]);
